/**
 * TenantPlanModal Component
 * Modal for changing tenant subscription plan
 */

import React, { useState } from 'react';
import { CreditCard, Check, Star, Zap, Crown } from 'lucide-react';
import { clsx } from 'clsx';
import { format, addDays } from 'date-fns';
import { Modal, Button, Input } from '../../../components/common';
import { useUpdateTenantPlan } from '../../../hooks/useSuperAdmin';
import type { TenantListItem, TenantPlan } from '../../../types/superadmin';

interface TenantPlanModalProps {
  tenant: TenantListItem;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const planOptions: {
  value: TenantPlan;
  label: string;
  description: string;
  icon: React.ElementType;
  features: string[];
  color: string;
}[] = [
  {
    value: 'trial',
    label: 'Trial',
    description: 'Try all features free for 30 days',
    icon: Star,
    features: ['All features', 'Up to 10 users', '30-day limit'],
    color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
  },
  {
    value: 'basic',
    label: 'Basic',
    description: 'Essential OKR management',
    icon: Check,
    features: ['Core OKR features', 'Up to 25 users', 'Email support'],
    color: 'text-primary-600 bg-primary-50 border-primary-200',
  },
  {
    value: 'professional',
    label: 'Professional',
    description: 'Advanced features for growing teams',
    icon: Zap,
    features: ['All Basic features', 'Unlimited users', 'Priority support', 'Analytics'],
    color: 'text-purple-600 bg-purple-50 border-purple-200',
  },
  {
    value: 'enterprise',
    label: 'Enterprise',
    description: 'Full platform with custom solutions',
    icon: Crown,
    features: ['All Pro features', 'Custom integrations', 'Dedicated support', 'SLA'],
    color: 'text-indigo-600 bg-indigo-50 border-indigo-200',
  },
];

export const TenantPlanModal: React.FC<TenantPlanModalProps> = ({
  tenant,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<TenantPlan>(tenant.plan);
  const [trialEndDate, setTrialEndDate] = useState(
    tenant.trialEndsAt
      ? format(new Date(tenant.trialEndsAt), 'yyyy-MM-dd')
      : format(addDays(new Date(), 30), 'yyyy-MM-dd')
  );

  const updatePlanMutation = useUpdateTenantPlan();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updatePlanMutation.mutateAsync({
        tenantId: tenant.id,
        data: {
          plan: selectedPlan,
          trialEndsAt:
            selectedPlan === 'trial'
              ? new Date(trialEndDate).toISOString()
              : undefined,
        },
      });
      onSuccess();
    } catch (error) {
      // Error handled by mutation
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Subscription Plan" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Current Plan */}
        <div className="p-4 bg-stone-100 rounded-lg">
          <p className="text-sm text-stone-500">Current Plan</p>
          <p className="text-lg font-medium text-stone-900">
            {tenant.name} - <span className="capitalize">{tenant.plan}</span>
          </p>
          {tenant.trialEndsAt && tenant.status === 'trial' && (
            <p className="text-sm text-orange-600">
              Trial ends {format(new Date(tenant.trialEndsAt), 'MMM d, yyyy')}
            </p>
          )}
        </div>

        {/* Plan Options */}
        <div className="grid grid-cols-2 gap-4">
          {planOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = selectedPlan === option.value;
            const isCurrent = tenant.plan === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setSelectedPlan(option.value)}
                className={clsx(
                  'flex flex-col p-4 rounded-lg border-2 transition-all text-left',
                  isSelected
                    ? option.color + ' border-current'
                    : 'bg-white border-stone-200 hover:border-stone-300'
                )}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon
                    className={clsx(
                      'w-5 h-5',
                      isSelected ? 'text-current' : 'text-stone-400'
                    )}
                  />
                  <span
                    className={clsx(
                      'font-medium',
                      isSelected ? 'text-current' : 'text-stone-900'
                    )}
                  >
                    {option.label}
                  </span>
                  {isCurrent && (
                    <span className="text-xs bg-stone-200 text-stone-600 px-2 py-0.5 rounded">
                      Current
                    </span>
                  )}
                </div>
                <p className="text-sm text-stone-500 mb-3">{option.description}</p>
                <ul className="space-y-1">
                  {option.features.map((feature, idx) => (
                    <li key={idx} className="text-xs text-stone-600 flex items-center gap-1">
                      <Check className="w-3 h-3 text-success-500" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        {/* Trial End Date (only for trial plan) */}
        {selectedPlan === 'trial' && (
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">
              Trial End Date
            </label>
            <Input
              type="date"
              value={trialEndDate}
              onChange={(e) => setTrialEndDate(e.target.value)}
              min={format(new Date(), 'yyyy-MM-dd')}
            />
            <p className="mt-1 text-xs text-stone-500">
              The date when the trial period will expire
            </p>
          </div>
        )}

        {/* Upgrade/Downgrade notice */}
        {selectedPlan !== tenant.plan && (
          <div
            className={clsx(
              'p-4 rounded-lg',
              planOptions.findIndex((p) => p.value === selectedPlan) >
                planOptions.findIndex((p) => p.value === tenant.plan)
                ? 'bg-success-50 border border-success-200'
                : 'bg-yellow-50 border border-yellow-200'
            )}
          >
            <div className="flex items-center gap-2">
              <CreditCard
                className={clsx(
                  'w-5 h-5',
                  planOptions.findIndex((p) => p.value === selectedPlan) >
                    planOptions.findIndex((p) => p.value === tenant.plan)
                    ? 'text-success-600'
                    : 'text-yellow-600'
                )}
              />
              <p
                className={clsx(
                  'font-medium',
                  planOptions.findIndex((p) => p.value === selectedPlan) >
                    planOptions.findIndex((p) => p.value === tenant.plan)
                    ? 'text-success-800'
                    : 'text-yellow-800'
                )}
              >
                {planOptions.findIndex((p) => p.value === selectedPlan) >
                planOptions.findIndex((p) => p.value === tenant.plan)
                  ? 'Upgrade'
                  : 'Downgrade'}{' '}
                from {tenant.plan} to {selectedPlan}
              </p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={updatePlanMutation.isPending || selectedPlan === tenant.plan}
          >
            {updatePlanMutation.isPending ? 'Updating...' : 'Update Plan'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
